import { ApolloError, useMutation } from '@apollo/client';
import {
	Button,
	Icon,
	SettingsGroup,
	SettingsItemCheckbox,
	SettingsItemTextField,
	Tooltip,
} from '@elipssolution/harfang';
import { mdiPencil, mdiPlus } from '@mdi/js';
import { alpha, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { generateErrorInformations } from '../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../utils/statusAnimationDelay';
import {
	CREATE_EXTERNAL_GROUP,
	CreateExternalGroupType,
	REMOVE_EXTERNAL_GROUP,
	RemoveExternalGroupType,
	UPDATE_EXTERNAL_GROUP,
	UpdateExternalGroupType,
} from '../../../api/externalGroup';
import { GroupType } from '../../../types/group';

const ActionWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
	alignItems: 'stretch',
});

const ErrorWrapper = styled('div')(({ theme: { palette, spacing, shape } }) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: spacing(3),

	padding: spacing(0, 3),

	borderRadius: shape.borderRadius * 1.5,

	backgroundColor: alpha(palette.error.main, 0.3),
}));

type FormFieldType = Pick<GroupType, 'isDefault' | 'name'>;

type ExternalGroupFormProps = {
	group?: Omit<GroupType, 'id' | 'name' | 'permissionModules'> & {
		id?: GroupType['id'];
		name?: GroupType['name'];
	};
	isFetchLoading: boolean;
	onError: (error: string) => void;
	onReplaceNeeded: (group: GroupType) => void;
	onRemoval: () => void;
};

const ExternalGroupForm = ({
	group,
	isFetchLoading = false,
	onError,
	onReplaceNeeded,
	onRemoval,
}: ExternalGroupFormProps) => {
	const { id: groupId, isAdministrator = false, isDefault, name: groupName } = group ?? {};

	const { onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		reset,
	} = useForm<FormFieldType>();

	const [isCreateExternalGroupSucceeded, setIsCreateExternalGroupSucceeded] = useState(false);
	const [isUpdateExternalGroupSucceeded, setIsUpdateExternalGroupSucceeded] = useState(false);

	const [createExternalGroup, { loading: isCreateExternalGroupLoading }] = useMutation<CreateExternalGroupType>(
		CREATE_EXTERNAL_GROUP,
		{
			onCompleted: () => {
				setIsCreateExternalGroupSucceeded(true);
				setTimeout(() => setIsCreateExternalGroupSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);
	const [updateExternalGroup, { loading: isUpdateExternalGroupLoading }] = useMutation<UpdateExternalGroupType>(
		UPDATE_EXTERNAL_GROUP,
		{
			onCompleted: () => {
				setIsUpdateExternalGroupSucceeded(true);
				setTimeout(() => setIsUpdateExternalGroupSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);
	const [removeExternalGroup, { error: removeExternalGroupMutationError, loading: isRemoveExternalGroupLoading }] =
		useMutation<RemoveExternalGroupType>(REMOVE_EXTERNAL_GROUP);

	const errorMessage = useMemo(
		() =>
			removeExternalGroupMutationError &&
			generateErrorInformations({ error: removeExternalGroupMutationError }).message,
		[removeExternalGroupMutationError],
	);

	const isLoading = useMemo(
		() =>
			isFetchLoading || isCreateExternalGroupLoading || isUpdateExternalGroupLoading || isRemoveExternalGroupLoading,
		[isCreateExternalGroupLoading, isFetchLoading, isRemoveExternalGroupLoading, isUpdateExternalGroupLoading],
	);

	const isMutationButtonDisabled = useMemo(
		() => isRemoveExternalGroupLoading || !isDirty || !isValid || isAdministrator,
		[isRemoveExternalGroupLoading, isDirty, isValid, isAdministrator],
	);

	const onSubmit = useCallback(
		(values: FormFieldType) => {
			const modeMutationInfoMap = new Map([
				['create', { inputValues: values, mutation: createExternalGroup, mutationName: 'createExternalGroup' }],
				[
					'update',
					{
						inputValues: {
							...values,
							id: groupId,
						},
						mutation: updateExternalGroup,
						mutationName: 'updateExternalGroup',
					},
				],
			]);

			const { mutation, mutationName = '', inputValues } = modeMutationInfoMap.get(groupId ? 'update' : 'create') || {};

			return mutation?.({
				variables: {
					[`${mutationName}Input`]: inputValues,
				},
			})
				.then(({ data }) => {
					if (!data) return;

					const { name, isDefault: isDefaultUpdated } = values;

					reset(values);

					if (name !== groupName || isDefaultUpdated) {
						const { id }: { id: string } = data[`${mutationName}` as keyof typeof data];

						onReplaceNeeded({
							...values,
							isAdministrator,
							id,
						});
					}
				})
				.catch((mutationError: ApolloError) =>
					onError(generateErrorInformations({ error: mutationError, resource: mutationName }).message),
				);
		},
		[createExternalGroup, groupId, updateExternalGroup, reset, groupName, onReplaceNeeded, isAdministrator, onError],
	);

	const startIcon = useMemo(() => <Icon path={groupId ? mdiPencil : mdiPlus} />, [groupId]);

	const disabledTooltipContent = useMemo(() => {
		if (isAdministrator) return 'Vous ne pouvez pas modifier le groupe administrateur.';
		if (isDefault) return 'Vous ne pouvez pas modifier le groupe par défaut.';

		return undefined;
	}, [isAdministrator, isDefault]);

	const submitButtonLabel = useMemo(() => {
		if (isCreateExternalGroupSucceeded) return 'Groupe ajouté';
		if (groupId) {
			if (isUpdateExternalGroupSucceeded) return 'Groupe modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [isCreateExternalGroupSucceeded, isUpdateExternalGroupSucceeded, groupId]);

	const handleExternalGroupRemoveButton = async () => {
		if (!groupId || !groupName) return undefined;

		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer le groupe ${groupName} ?`,
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return null;

		const result = await removeExternalGroup({
			variables: {
				id: groupId,
			},
		});

		onRemoval?.();

		return result;
	};

	// Setting form state
	useEffect(
		() =>
			reset({
				name: groupName,
				isDefault,
			}),
		[reset, groupName, isDefault],
	);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${group ? 'modification' : 'création'} du groupe ?`,
		);
	}, [group, isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange]);

	return (
		<>
			<SettingsGroup>
				<Controller
					control={control}
					render={({ field }) => (
						<SettingsItemTextField
							{...field}
							description="Nom du groupe."
							disabled={isAdministrator || isFetchLoading}
							label="Nom"
							required
						/>
					)}
					rules={{ required: true }}
					name="name"
				/>

				<Controller
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, le groupe est celui par défaut."
							disabled={isAdministrator || isDefault || isFetchLoading}
							disabledTooltipContent={disabledTooltipContent}
							label="Par défaut"
						/>
					)}
					name="isDefault"
				/>
			</SettingsGroup>

			{!isAdministrator && !isFetchLoading && (
				<ActionWrapper>
					<Button
						disabled={isMutationButtonDisabled}
						onClick={handleSubmit(onSubmit)}
						startIcon={startIcon}
						variant="contained"
					>
						{submitButtonLabel}
					</Button>

					{errorMessage && <ErrorWrapper>{errorMessage}</ErrorWrapper>}

					{groupId && (
						<Tooltip content={isDefault ? 'Un groupe par défaut ne peux pas être supprimé' : ''}>
							<Button
								color="error"
								disabled={isLoading || isDefault}
								onClick={handleExternalGroupRemoveButton}
								variant="outlined"
							>
								Supprimer
							</Button>
						</Tooltip>
					)}
				</ActionWrapper>
			)}
		</>
	);
};

export default ExternalGroupForm;
